import React from 'react';
import { observable, action, toJS } from "mobx";
import { observer } from "mobx-react";
import Grid from "@material-ui/core/Grid";
import Skeleton from '@material-ui/lab/Skeleton';
import BaseScenarioDashboardView from "../BaseScenarioDashboardView";
import Header from "../../components/utils/Header";
import ScenarioSelect from "../scenarios/ScenarioSelect";
import DataFrame from "../../components/utils/DataFrame";
import { formatFloat } from "../../utils";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import Scenario from "../../api/Scenario";
import DataFrameDownload from '../../components/utils/DataFrameDownload';
import MultiSelectListView from '../../components/MultiSelectListView';
import MultiSelectList from '../../store/MultiSelectList';
import { removeWhitespaces } from '../../utils';


@observer
class RawAndPacksBom extends BaseScenarioDashboardView {
  static apiSubPath = '/bom_explosion/';

  @observable reverse = false;
  @observable scenario = null;
  @observable matCods = [];
  @observable quantity = 1.;

  constructor(props) {
    super(props);
    this.multiSelectList = new MultiSelectList('MAT_CODs');
    this.multiSelectList.limit = 50;
  }

  _setData(data) {
    const selectedMatCods = data.mat_cod === null? [] : typeof data.mat_cod === 'string'? data.mat_cod.split() : data.mat_cod;
    removeWhitespaces(selectedMatCods)
    if(data.mat_cod_choices){
      const availableMatCods = toJS(this.data.mat_cod_choices).map(keys => keys.value);
      this.multiSelectList.update(availableMatCods, selectedMatCods)
    }

    this.reverse = data.reverse;
    this.scenario = data.scenario;
    this.matCods = selectedMatCods;
    this.quantity = data.quantity ? data.quantity.toFixed(2) : data.quantity;
  }

  _getQueryParams() {
    return {
      reverse: this.reverse,
      scenario: this.scenario,
      mat_cod: toJS(this.matCods),
    };
  }

  onReverseChange = this.genericToggleHandler('reverse');
  onScenarioChange = this.genericChangeHandler('scenario');
  onMatCodChange = this.genericChangeValueHandler('matCods');
  onQuantityChange = this.genericChangeHandler('quantity', false);

  @action clear = () => this.matCods = []

  renderControls() {
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          BOM explosion
          <Switch 
            checked={this.reverse}
            onChange={this.onReverseChange}
            onInput={() => this.clear()}
          />
          Reverse BOM
        </Grid>
        <Grid item>
          <ScenarioSelect
            scenarioId={this.scenario}
            choices={this.data.scenario_choices}
            onScenarioChange={this.onScenarioChange}
            onScenarioSelect = {() => this.clear()}
          />
        </Grid>
        <Grid item>
          <TextField
            type="number" label="Quantity" value={this.quantity} onChange={this.onQuantityChange} variant="outlined"
          />
        </Grid>
        <Grid item>
          <MultiSelectListView
            selectedValues={toJS(this.matCods)}
            multiSelectList={this.multiSelectList}
            downloadConfig={{
              href: Scenario.getApiUrl('/bom_explosion/?'+ this.getQueryString()), 
              permission: "athena.download_raw_packs_bom",
              param: 'mat_cod',
            }}
            onValueChange={(event, values) => {
              this.onMatCodChange(event,values)
            }}
          />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/bom_explosion/?'+ this.getQueryString())}
            permission="athena.download_raw_packs_bom"
            tooltipText="Download selected MAT_CODs"
          />
        </Grid>
      </Grid>
    );
  }

  formatData = (serializedDataFrame, columnIndex, rowIndex, value) => {
    if (serializedDataFrame.columns[columnIndex][0] === 'Quantity') {
      value = formatFloat(value * this.quantity, 2);
    }
    if (this.data.header_row.includes(rowIndex)) {
      return <strong>{value}</strong>;
    }
    return value;
  };

  getColumnProps = (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => {
    if (columnName === 'Quantity') {
      return { align: 'right' };
    }
    return {};
  };

  getDataProps = (serializedDataFrame, columnIndex, rowIndex, value) => {
    if (serializedDataFrame.columns[columnIndex][0] === 'Quantity') {
      return { align: 'right' };
    }
    return {};
  };

  render() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={800} />
    }
    return (
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Grid container spacing={2} wrap="nowrap" alignItems="center" justify="space-between">
            <Grid item><Header title="Raw & packs BOM" /></Grid>
            <Grid item>{this.renderControls()}</Grid>
          </Grid>
        </Grid>
        <Grid item>
          {
            this.data.dataframe.data.length === 0
              ?
              'Please select a MAT_COD'
              :
              (
                <DataFrame
                  serializedDataFrame={this.data.dataframe}
                  maxHeight={700}
                  formatData={this.formatData}
                  getColumnProps={this.getColumnProps}
                  getDataProps={this.getDataProps}
                />
              )
          }
        </Grid>
      </Grid>
    );
  }
}


export default RawAndPacksBom;
